import { useEffect, useState } from "react";
import { Sparkles, Loader2, Check, Edit3 } from "lucide-react";
import { patchPersonalization } from "@/lib/api";
import { toast } from "sonner";

// Same option lists as the Onboarding page — kept inline (instead of
// importing) so this section can be reused independently and so the
// frontend isn't reaching across page files.
const USER_TYPES = [
    { key: "first_time_buyer", label: "First-Time Homebuyer" },
    { key: "repeat_buyer", label: "Repeat Homebuyer" },
    { key: "homeowner", label: "Homeowner" },
    { key: "investor", label: "Real Estate Investor" },
    { key: "agent", label: "Real Estate Agent" },
    { key: "property_manager", label: "Property Manager" },
    { key: "contractor", label: "Contractor" },
    { key: "inspector", label: "Inspector" },
    { key: "other", label: "Other" },
];
const AGE_RANGES = [
    { key: "under_25", label: "Under 25" },
    { key: "25_34", label: "25–34" },
    { key: "35_44", label: "35–44" },
    { key: "45_54", label: "45–54" },
    { key: "55_64", label: "55–64" },
    { key: "65_plus", label: "65+" },
    { key: "prefer_not_to_say", label: "Prefer not to say" },
];
const ACQUISITION_SOURCES = [
    { key: "google", label: "Google Search" },
    { key: "pinterest", label: "Pinterest" },
    { key: "facebook", label: "Facebook" },
    { key: "instagram", label: "Instagram" },
    { key: "linkedin", label: "LinkedIn" },
    { key: "referral", label: "Referral" },
    { key: "agent", label: "Real Estate Agent" },
    { key: "inspector", label: "Home Inspector" },
    { key: "contractor", label: "Contractor" },
    { key: "email", label: "Email" },
    { key: "other", label: "Other" },
];
const PRIMARY_GOALS = [
    { key: "understand_report", label: "Understand an inspection report" },
    { key: "estimate_costs", label: "Estimate repair costs" },
    { key: "prepare_negotiation", label: "Prepare for negotiation" },
    { key: "compare_contractors", label: "Compare contractors" },
    { key: "evaluate_investment", label: "Evaluate an investment property" },
    { key: "research_issues", label: "Research home issues" },
    { key: "other", label: "Other" },
];

// Phase 2 — progressive profiling enums
const PROPERTY_STAGES = [
    { key: "researching", label: "Just researching" },
    { key: "scheduling_inspection", label: "Scheduling an inspection" },
    { key: "inspection_completed", label: "Inspection completed" },
    { key: "under_contract", label: "Under contract" },
    { key: "negotiating_repairs", label: "Negotiating repairs" },
    { key: "closing_soon", label: "Closing soon" },
    { key: "already_own", label: "Already own the property" },
];
const PROPERTY_TYPES = [
    { key: "single_family", label: "Single-family home" },
    { key: "condo", label: "Condo" },
    { key: "townhome", label: "Townhome" },
    { key: "duplex", label: "Duplex" },
    { key: "multifamily", label: "Multifamily" },
    { key: "commercial", label: "Commercial" },
    { key: "other", label: "Other" },
];
const PROPERTIES_OWNED = [
    { key: "none", label: "None yet" },
    { key: "one", label: "1" },
    { key: "two_to_five", label: "2–5" },
    { key: "six_to_twenty", label: "6–20" },
    { key: "twenty_plus", label: "20+" },
];
const US_STATES_OPTIONS = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
    "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
    "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
    "VA","WA","WV","WI","WY","DC",
].map((s) => ({ key: s, label: s }));

const FIELDS = [
    { key: "user_type", label: "Who are you?", options: USER_TYPES },
    { key: "age_range", label: "Age range", options: AGE_RANGES },
    { key: "acquisition_source", label: "How you found us", options: ACQUISITION_SOURCES },
    { key: "primary_goal", label: "Primary goal", options: PRIMARY_GOALS },
    { key: "property_stage", label: "Property stage", options: PROPERTY_STAGES },
    { key: "property_type", label: "Property type", options: PROPERTY_TYPES },
    { key: "property_state", label: "Property state", options: US_STATES_OPTIONS },
    {
        key: "properties_owned",
        label: "Investment properties owned",
        options: PROPERTIES_OWNED,
        // Only shown when user_type === 'investor' (or a derived
        // investor segment) — gated below in render.
        investorOnly: true,
    },
];

function labelFor(field, value) {
    if (!value) return null;
    const opt = field.options.find((o) => o.key === value);
    return opt ? opt.label : value;
}

export default function PersonalizationSection({ user, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        user_type: user?.user_type || null,
        age_range: user?.age_range || null,
        acquisition_source: user?.acquisition_source || null,
        primary_goal: user?.primary_goal || null,
        property_stage: user?.property_stage || null,
        property_type: user?.property_type || null,
        property_state: user?.property_state || null,
        properties_owned: user?.properties_owned || null,
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        // Re-sync if user prop changes (e.g. after a refresh post-save).
        setDraft({
            user_type: user?.user_type || null,
            age_range: user?.age_range || null,
            acquisition_source: user?.acquisition_source || null,
            primary_goal: user?.primary_goal || null,
            property_stage: user?.property_stage || null,
            property_type: user?.property_type || null,
            property_state: user?.property_state || null,
            properties_owned: user?.properties_owned || null,
        });
    }, [
        user?.user_type, user?.age_range, user?.acquisition_source, user?.primary_goal,
        user?.property_stage, user?.property_type, user?.property_state, user?.properties_owned,
    ]);

    const showInvestorField = draft.user_type === "investor"
        || ["small_investor", "growing_investor", "portfolio_investor", "investor"]
            .includes(user?.audience_segment);

    const visibleFields = FIELDS.filter(
        (f) => !f.investorOnly || showInvestorField,
    );

    const save = async () => {
        setSaving(true);
        try {
            await patchPersonalization(draft);
            toast.success("Personalization saved.");
            setEditing(false);
            onSaved?.();
        } catch {
            toast.error("Couldn't save. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section
            className="bl-card p-5 sm:p-6"
            data-testid="personalization-section"
        >
            <header className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div className="flex items-start gap-2.5">
                    <Sparkles className="h-4 w-4 text-bl-primary mt-1" />
                    <div>
                        <h2 className="font-display font-semibold text-lg text-bl">
                            Personalization
                        </h2>
                        <p className="text-xs text-bl-muted mt-1 max-w-md">
                            Optional answers used only to improve Buyer&apos;s
                            Leverage. Never shared or sold.
                        </p>
                    </div>
                </div>
                {!editing && (
                    <button
                        type="button"
                        onClick={() => setEditing(true)}
                        className="bl-btn-ghost text-xs gap-1.5"
                        data-testid="personalization-edit-btn"
                    >
                        <Edit3 className="h-3 w-3" />
                        Edit
                    </button>
                )}
            </header>

            {!editing && (
                <dl
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    data-testid="personalization-readonly"
                >
                    {visibleFields.map((f) => (
                        <div key={f.key}>
                            <dt className="text-[10px] uppercase tracking-[0.18em] text-bl-muted mb-1">
                                {f.label}
                            </dt>
                            <dd
                                className="text-sm text-bl"
                                data-testid={`personalization-${f.key}-value`}
                            >
                                {labelFor(f, draft[f.key]) || (
                                    <span className="text-bl-muted italic">
                                        Not set
                                    </span>
                                )}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}

            {editing && (
                <div className="space-y-5" data-testid="personalization-form">
                    {visibleFields.map((f) => (
                        <div key={f.key}>
                            <label className="block text-[11px] uppercase tracking-[0.18em] text-bl-muted mb-2">
                                {f.label}
                            </label>
                            <select
                                value={draft[f.key] || ""}
                                onChange={(e) =>
                                    setDraft((prev) => ({
                                        ...prev,
                                        [f.key]: e.target.value || null,
                                    }))
                                }
                                disabled={saving}
                                className="w-full bg-bl-bg border border-bl rounded px-3 py-2 text-sm"
                                data-testid={`personalization-${f.key}-select`}
                            >
                                <option value="">— Not set —</option>
                                {f.options.map((o) => (
                                    <option key={o.key} value={o.key}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    ))}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={save}
                            disabled={saving}
                            className="bl-btn-primary text-xs gap-1.5"
                            data-testid="personalization-save-btn"
                        >
                            {saving ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                                <Check className="h-3 w-3" />
                            )}
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => setEditing(false)}
                            disabled={saving}
                            className="bl-btn-ghost text-xs"
                            data-testid="personalization-cancel-btn"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
